import { useState } from 'react';
import {
  FaUserGraduate,
  FaLock,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  setUser,
  setLoading,
  setError
} from '../../store/userSlice';
import { postData } from '../../services/apiService';
import {
  setEncryptedCookie
} from '../../utils/cookieEncryption';

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [fieldErrors, setFieldErrors] = useState({
    userId: false,
    password: false
  });

  const getErrorMessage = (err) => {
    if (err.response?.data?.message) {
      return err.response.data.message;
    }

    if (err.message) {
      return err.message;
    }

    return 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError('');

    setFieldErrors({
      userId: false,
      password: false
    });

    // Validation
    if (!userId || !password) {
      setFormError(
        'Please enter both userId/student ID and password'
      );

      setFieldErrors({
        userId: !userId,
        password: !password
      });

      return;
    }

    setIsLoading(true);
    dispatch(setLoading(true));

    try {
      const loginPayload = {
        username: userId,
        password: password
      };

      const data = await postData(
        '/Authentication/login',
        loginPayload
      );

      if (
        data &&
        data.result &&
        data.result.accessToken
      ) {
        const cookieOptions = {
          secure:
            process.env.NODE_ENV === 'production',
          sameSite: 'Strict',
          path: '/',
          expires: 1
        };

        const accessToken =
          data.result.accessToken;

        const refreshToken =
          data.result.refreshToken || null;

        const user =
          data.result.user || {};

        // Store encrypted access token
        const tokenStored =
          setEncryptedCookie(
            '_at',
            accessToken,
            cookieOptions
          );

        // Store encrypted refresh token
        if (refreshToken) {
          setEncryptedCookie(
            '_rt',
            refreshToken,
            cookieOptions
          );
        }

        if (!tokenStored) {
          throw new Error(
            'Failed to store authentication tokens securely'
          );
        }

        // Store user data in Redux
        dispatch(
          setUser({
            user: {
              user_id: user.user_id,
              roll_no: user.roll_no,
              inst_id: user.inst_id,
              inst_name: user.inst_name,
              user_name: user.user_name,
              isFirstLogin: user.isFirstLogin,
              email: user.email,
              phone: user.phone,
              ...user
            },
            accessToken: accessToken,
            refreshToken: refreshToken
          })
        );

        // Navigate to dashboard
        navigate('/dashboard', {
          replace: true
        });
      } else {
        const errorMsg =
          data?.message ||
          'Login failed. Invalid server response.';

        setFormError(errorMsg);
        dispatch(setError(errorMsg));

        setFieldErrors({
          userId: true,
          password: true
        });
      }
    } catch (err) {
      console.error('Login error:', err);

      const errorMessage =
        getErrorMessage(err);

      setFormError(errorMessage);

      dispatch(setError(errorMessage));

      const lowerCaseError =
        errorMessage.toLowerCase();

      if (
        lowerCaseError.includes('user') ||
        lowerCaseError.includes('email')
      ) {
        setFieldErrors({
          userId: true,
          password: false
        });
      } else if (
        lowerCaseError.includes('password')
      ) {
        setFieldErrors({
          userId: false,
          password: true
        });
      } else {
        setFieldErrors({
          userId: true,
          password: true
        });
      }
    } finally {
      setIsLoading(false);
      dispatch(setLoading(false));
    }
  };

  const handleUserIdChange = (e) => {
    setUserId(e.target.value);

    if (fieldErrors.userId) {
      setFieldErrors((prev) => ({
        ...prev,
        userId: false
      }));
    }

    if (formError) {
      setFormError('');
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);

    if (fieldErrors.password) {
      setFieldErrors((prev) => ({
        ...prev,
        password: false
      }));
    }

    if (formError) {
      setFormError('');
    }
  };

  return (
    <div className="relative">

      {/* =====================================================
          LOGIN CARD
      ===================================================== */}
      <div className="bg-white rounded-xl shadow-xl overflow-hidden">

        {/* ================= HEADER ================= */}
        <div className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] p-8 text-center">

          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">

            <FaUserGraduate
              className="text-white text-2xl"
            />

          </div>

          <h1 className="text-2xl font-bold text-white">
            Student Portal Login
          </h1>

          <p className="text-blue-100 mt-2">
            Welcome back! Please enter your details
          </p>

        </div>

        {/* ================= LOGIN FORM ================= */}
        <form
          className="p-8 space-y-6"
          onSubmit={handleSubmit}
        >

          <div className="space-y-4">

            {/* ================= ERROR ================= */}
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {formError}
              </div>
            )}

            {/* ================= USER ID ================= */}
            <div className="relative">

              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUserGraduate className="text-gray-400" />
              </div>

              <input
                type="text"
                value={userId}
                onChange={handleUserIdChange}
                autoComplete="username"
                placeholder="Enter Your Roll Number"
                disabled={isLoading}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${fieldErrors.userId
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-300 focus:border-[#0d4f5c] focus:ring-[#0d4f5c]/20'
                  } focus:outline-none focus:ring-2 transition duration-300`}
              />

            </div>

            {/* ================= PASSWORD ================= */}
            <div className="relative">

              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="text-gray-400" />
              </div>

              <input
                type={
                  showPassword
                    ? 'text'
                    : 'password'
                }
                value={password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
                placeholder="DDMMMYYYY (e.g., 29FEB2002)"
                disabled={isLoading}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border ${fieldErrors.password
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-300 focus:border-[#0d4f5c] focus:ring-[#0d4f5c]/20'
                  } focus:outline-none focus:ring-2 transition duration-300`}
              />

              {/* Password Visibility */}
              <button
                type="button"
                onClick={() =>
                  setShowPassword(
                    !showPassword
                  )
                }
                disabled={isLoading}
                aria-label={
                  showPassword
                    ? 'Hide password'
                    : 'Show password'
                }
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-[#0d4f5c] focus:outline-none transition"
              >

                {showPassword ? (
                  <FaEyeSlash />
                ) : (
                  <FaEye />
                )}

              </button>

            </div>

          </div>

          {/* <div className="flex items-center justify-end">

            <a
              href="forgot-password"
              className="text-sm text-[#0d4f5c] hover:text-[#1a7a8a] hover:underline focus:outline-none rounded"
            >
              Forgot password?
            </a>

          </div> */}

          {/* ================= SIGN IN BUTTON ================= */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-lg text-white font-semibold ${isLoading
              ? 'bg-[#0d4f5c]/60 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] hover:shadow-lg hover:shadow-[#0d4f5c]/30 active:scale-[0.98]'
              } transition duration-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0d4f5c] focus:ring-offset-2`}
          >

            {isLoading ? (
              <>
                {/* Spinner */}
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />

                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />

                </svg>

                Signing in...
              </>
            ) : (
              'Sign In'
            )}

          </button>

        </form>
        <div className="bg-gray-50 px-8 py-6 text-center border-t border-gray-200">
          <p className="text-sm text-gray-600">
            By continuing, you agree to our{' '}
            <a
              href="#privacy-policy"
              className="font-medium text-[#0d4f5c] hover:text-[#1a7a8a] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0d4f5c]/20 rounded"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="#terms"
              className="font-medium text-[#0d4f5c] hover:text-[#1a7a8a] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0d4f5c]/20 rounded"
            >
              Terms & Conditions
            </a>
          </p>
        </div>
      </div>

      <footer
        className="
    fixed
    bottom-0
    left-0
    right-0
    z-50
    sm:px-5
    bg-black
    backdrop-blur-md
    border-t
    border-white/10
  "
      >
        <div
          className="
      max-w-7xl
      mx-auto
      flex
      flex-col
      sm:flex-row
      items-center
      justify-between
      gap-2
      sm:gap-4
    "
        >
          <div></div>
          <p
            className="
        text-white
        text-[10px]
        xs:text-xs
        sm:text-sm
        font-medium
        text-center
        leading-tight
        order-2
        sm:order-1
      "
          >
            © 2026 Directorate of Technical Education (DOTE).
            <span className="hidden sm:inline"> </span>
            All Rights Reserved.
          </p>

          {/* Powered By */}
          <div
            className="
        flex
        flex-row
        sm:flex-col
        items-center
        justify-center
        gap-1
        sm:gap-0
        order-1
        sm:order-2
      "
          >
            <span
              className="
          text-white/70
          text-[9px]
          sm:text-[10px]
          font-medium
          leading-none
        "
            >
              Powered by
            </span>

            <span
              className="
          text-white
          text-[10px]
          sm:text-xs
          font-bold
          leading-none
          whitespace-nowrap
        "
            >
              PSG Software Technologies
            </span>
          </div>

        </div>
      </footer>


    </div>
  );
};

export default LoginForm;